import type { ReactNode } from 'react';
import { adSecondsFor } from '@apm/contracts';
import type { AdPolicy } from '@apm/contracts';
import { formatClock, formatPrice } from '@apm/i18n';
import type { Translator } from '@apm/i18n';
import type { ProductListRow } from '../product-list.js';
import type { MessageKey } from '../i18n/ko.js';
import { useLocale, useT } from '../i18n/LocaleProvider.js';

/**
 * 편성 상품 목록 (v11, `docs/27` §1).
 *
 * **특허 ④ 가 보호하는 둘 중 지금까지 없던 앞쪽이다** — 「상품 목록을 조망하고 직접 선택」.
 * 뒤쪽(제2 플레이리스트로 재생 지점을 즉시 이동)은 `selectItem()` 이 M5 부터 하고 있었고,
 * 그것을 부르는 화면이 없어 `Event` 에 `select` 가 한 건도 없었다 (`TASKS.md` M7).
 *
 * **여기 오는 목록은 이미 걸러져 있다.** 그 채널의 플레이리스트 그대로이고 서버가 재생
 * 가능한 것만 준다 (절대 규칙 1·34) — **고른 것이 재생되지 않는 경우가 원리적으로 없다.**
 *
 * **네트워크 호출이 없다.** 제목·가격·썸네일이 `PlaylistItem` 에 이미 다 있다
 * (`InfoOverlay` 와 같은 이유). 상품 시트는 `productId` 만 받아 서버를 다시 부르는데,
 * 그쪽은 **판매처 주소**가 그 조회에서 오기 때문이고 목록은 아무 데도 나가지 않는다.
 */
export function ProductListScreen({
  rows,
  onPick,
  resume,
  adPolicy,
  entryProductId,
}: {
  rows: readonly ProductListRow[];
  /** 고른 상품. **같은 상품이면 전환하지 않는 판정은 호출부가 한다** (`resolvePick`) */
  onPick: (productId: string) => void;
  /**
   * 이어볼 지점 (v18). 없으면(비로그인·처음 보는 채널) 아무 행에도 표시가 없다.
   *
   * **회차별 진행을 받지 않는다** — 그런 것이 더 이상 없다.
   */
  resume?: { productId: string; positionSec: number } | null;
  /**
   * v21. **이 채널의 광고 정책.** `null` 이면 배지가 없고 **그 채널의 시트는 한 픽셀도
   * 안 바뀐다.**
   *
   * 시트가 **설정 토글까지 반영해** 넘긴다 — 끈 채로 배지만 남으면 **없는 시간을 말하는
   * 화면**이 된다.
   */
  adPolicy?: AdPolicy | null;
  /**
   * v39. 「콘텐츠」 2단계에 들어갈 때 포커스가 앉을 회차(`episodeEntryFocus`). 없으면 표시도 없다 — 지금의 시트(`'flat'`)는
   * 이 칸을 주지 않아 DOM 이 그대로다.
   */
  entryProductId?: string | null;
}) {
  const t = useT();
  const { locale } = useLocale();

  return (
    <ul className="products" data-testid="product-list">
      {rows.map((row) => (
        <li key={row.productId}>
          <button
            type="button"
            className="products__item"
            data-testid="product-item"
            data-product-id={row.productId}
            aria-current={row.current ? 'true' : undefined}
            data-content-entry={
              entryProductId != null && entryProductId === row.productId ? 'true' : undefined
            }
            onClick={() => {
              onPick(row.productId);
            }}
          >
            {/* 썸네일은 인코딩이 뽑아 둔 것이다. `alt` 를 비우는 이유는 바로 옆에 제목이
                있어서다 — 읽어 주면 같은 말이 두 번 나온다. */}
            <img className="products__thumb" src={row.thumbnailUrl} alt="" />
            <span className="products__body">
              {/* `data-i18n="data"` — 관리자가 넣은 값이라 **번역 대상이 아니다**
                  (`docs/22` §1). 영어 화면에도 한국어로 남는 것이 의도이고, 한글
                  훑기(`verify-locale.mjs`)가 이 표시를 보고 건너뛴다. 빠뜨리면 그
                  검사가 즉시 「번역 누락」으로 신고한다 — 그 방향의 실패가 옳다. */}
              {/*
                일반 채널은 카테고리 자리에 **회차 라벨**이 온다. 라벨은 사전을 거치므로
                `data-i18n="data"` 를 붙이지 않는다 — 붙이면 영어 화면에서 그 틀이
                검사 밖으로 나간다.
              */}
              {row.content === null ? (
                <span className="products__category" data-i18n="data">
                  {row.category}
                </span>
              ) : (
                <span className="products__category">{episodeLabelFor(t, row)}</span>
              )}
              <span className="products__name" data-i18n="data">
                {row.title}
              </span>
              {/*
                줄거리는 **지금 재생 중인 항목만** 펼친다 (v16, `docs/38` §6.4).
                전부 펼치면 리모컨으로 훑을 때 한 화면에 두 줄밖에 안 들어온다.
              */}
              {row.current && row.content?.synopsis != null && (
                <span className="products__synopsis" data-i18n="data">
                  {row.content.synopsis}
                </span>
              )}
              {/*
                가격은 **공유 포맷터**를 쓴다. 여기서 `원` 을 직접 쓰면 v9 이 겪은
                「영어 화면에 한국어가 남았다」가 재현된다 (`docs/24` §5.1-b).

                **일반 채널에는 가격이 없다** (v16). 0 을 「0원」으로 그리면 「무료 상품」
                처럼 읽힌다 — 그 자리에 **이어볼 회차 표시**가 온다(v18. 그 한 행에만).
              */}
              {row.content === null ? (
                <span className="products__price">{formatPrice(row.price, locale)}</span>
              ) : resume != null && resume.productId === row.productId ? (
                <ResumeMark positionSec={resume.positionSec} t={t} />
              ) : null}
              {/*
                v21. **두 값으로 그린다** (절대 규칙 57 · D18) — 「42분」과 「+광고 6분」이
                따로다. 더해서 「48분」 하나로 보이면 시청자가 그 값을 이야기의 길이로 읽고,
                이어보기·청크가 쓰는 축과 섞인다.

                `adSecondsFor` 를 **계약에서 가져온다** — 여기서 다시 쓰면 「콘솔이 말하는
                광고량과 실제로 나가는 양이 다르다」의 화면판이 된다 (D15).
              */}
              {row.content !== null && row.durationSec > 0 && (
                <span className="products__duration">
                  {t('episodes.duration', { n: Math.round(row.durationSec / 60) })}
                  {adBadgeFor(adPolicy ?? null, row.durationSec, t)}
                </span>
              )}
            </span>
            {/* 지금 재생 중인 줄. **글자로도 알 수 있어야 한다** — 리모컨으로 3m 떨어져
                보면 테두리만으로는 갈리지 않는다. */}
            {row.current && <span className="products__now">{t('products.nowPlaying')}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * 이어볼 회차 표시 (v18, 특허 청구항 10 의 「시청 진행 정보」가 남은 자리).
 *
 * ── v16 은 회차마다 진행률 바를 그렸다 ──────────────────────
 *
 * 진행이 **회차마다** 있었기 때문이고, v18 은 (계정, 채널) **한 지점**만 갖는다. 그래서
 * 그릴 것이 **그 한 회차뿐**이다 — 나머지 행에는 아무 표시도 없다.
 *
 * **없는 데이터를 그리지 않는 것이 요점이다.** 「시청 완료」·「25%」는 이제 근거가 없고,
 * 근거 없는 표시는 **틀린 것보다 나쁘다**(시청자가 그것을 믿고 고른다).
 */
function ResumeMark({ positionSec, t }: { positionSec: number; t: Translator<MessageKey> }) {
  return (
    <span className="products__progress">
      <span className="products__progress-text">
        {t('episodes.resumeAt', { time: formatClock(positionSec) })}
      </span>
    </span>
  );
}

/**
 * v21. 「+광고 n분」 배지. **정책이 없거나 붙는 광고가 0초면 아무것도 그리지 않는다** —
 * 그 채널의 시트는 한 픽셀도 안 바뀐다.
 */
function adBadgeFor(
  policy: AdPolicy | null,
  durationSec: number,
  t: Translator<MessageKey>,
): ReactNode {
  if (policy === null) return null;
  const seconds = adSecondsFor(policy, durationSec);
  if (seconds <= 0) return null;
  return (
    <span className="products__ad-load" data-testid="ad-load-badge">
      {t('watch.adLoad', { n: Math.round(seconds / 60) })}
    </span>
  );
}

/** `episode-label.ts` 의 것을 행에 맞춰 부른다 */
function episodeLabelFor(t: Translator<MessageKey>, row: ProductListRow): string {
  const content = row.content;
  if (content === null || content.episodeNo === null) return row.category;
  if (content.seasonNo === null) {
    return t('episodes.labelNoSeason', { episode: content.episodeNo });
  }
  return t('episodes.label', { season: content.seasonNo, episode: content.episodeNo });
}
